import React from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  ArrowLeft, 
  Edit, 
  Trash2, 
  Users, 
  Briefcase, 
  MapPin, 
  Calendar, 
  DollarSign, 
  Target, 
  Clock, 
  GraduationCap, 
  FileText, 
  Settings,
  AlertCircle,
  Search,
  Copy,
  Check
} from 'lucide-react';
import { toast } from 'react-hot-toast';

import { apiClient } from '../lib/api/client';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { Button } from '../components/ui/Button';
import { useConfirmation } from '../components/ui/ConfirmationDialog';
import { useAuth } from '../contexts/AuthContext';

const RequirementDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { confirm } = useConfirmation();
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();

  // Get previous page from location state
  const previousPage = location.state?.from || '/requirements';

  // Data fetching
  const { data: requirement, isLoading, error } = useQuery({
    queryKey: ['requirement', id],
    queryFn: () => apiClient.getRequirement(id!),
    enabled: !!id,
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (requirementId: string) => apiClient.deleteRequirement(requirementId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['requirements'] });
      toast.success('Requirement deleted successfully');
      navigate(previousPage);
    },
    onError: (error: any) => {
      console.error('Delete failed:', error);
      toast.error('Failed to delete requirement');
    },
  });

  // Regenerate Boolean Queries mutation
  const regenerateMutation = useMutation({
    mutationFn: (requirementId: string) => apiClient.regenerateBooleanQueries(requirementId),
    onSuccess: () => {
      toast.success('Generating 5-level queries... Refresh in ~5 minutes to see results');
      // Optionally refetch after a delay
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['requirement', id] });
      }, 300000); // 5 minutes
    },
    onError: (error: any) => {
      console.error('Regeneration failed:', error);
      toast.error(error?.response?.data?.detail || 'Failed to start query regeneration');
    },
  });

  // Handlers
  const handleBack = () => {
    navigate(previousPage);
  };

  const handleRegenerateQueries = () => {
    if (!id) return;
    
    confirm({
      title: 'Generate 5-Level Boolean Queries',
      description: 'This will generate new AI-powered Boolean search queries with 5 precision levels (Query 1 to Query 5). The process takes ~5 minutes. Continue?',
      variant: 'default',
      onConfirm: () => {
        regenerateMutation.mutate(id);
      },
    });
  };

  const handleEdit = () => {
    navigate(`/requirements/${id}/edit`);
  };

  const handleDelete = () => {
    if (!requirement) return;
    
    confirm({
      title: 'Delete Requirement',
      description: `Are you sure you want to delete "${requirement.title}"? This action cannot be undone.`,
      variant: 'destructive',
      confirmText: 'Delete',
      onConfirm: () => deleteMutation.mutate(requirement.id),
    });
  };

  const handleViewCandidates = () => {
    navigate(`/requirements/${id}/candidates`);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background-200 dark:from-[#141414] via-background-100 dark:via-[#1A1A1A] to-background-200 dark:to-[#141414] flex items-center justify-center">
        <div className="glass-card backdrop-blur-xl border-glass-border dark:border-glass-border-dark-mode shadow-2xl rounded-xl p-8 text-center">
          <LoadingSpinner size="lg" variant="ai" className="text-primary-500 mx-auto mb-4" />
          <p className="text-text-enterprise-primary dark:text-text-enterprise-primary">Loading requirement details...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !requirement) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background-200 dark:from-[#141414] via-background-100 dark:via-[#1A1A1A] to-background-200 dark:to-[#141414] flex items-center justify-center">
        <div className="glass-card backdrop-blur-xl border-glass-border dark:border-glass-border-dark-mode shadow-2xl rounded-xl p-8 text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-error-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-text-enterprise-primary dark:text-text-enterprise-primary mb-2">Requirement Not Found</h2>
          <p className="text-text-enterprise-secondary dark:text-text-enterprise-secondary mb-6">
            The requirement you're looking for doesn't exist or has been deleted.
          </p>
          <Button onClick={handleBack} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Requirements
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background-200 dark:from-[#141414] via-background-100 dark:via-[#1A1A1A] to-background-200 dark:to-[#141414]">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-gradient-to-r from-glass-surface/90 dark:from-glass-surface-dark-mode/90 via-glass-surface/95 dark:via-glass-surface-dark-mode/95 to-glass-surface/90 dark:to-glass-surface-dark-mode/90 backdrop-blur-xl border-b border-glass-border dark:border-glass-border-dark-mode/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Back Button & Title */}
            <div className="flex items-center gap-4">
              <Button
                onClick={handleBack}
                variant="ghost"
                className="p-2 hover:bg-glass-surface/50 dark:hover:bg-glass-surface-dark-mode/50 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-text-enterprise-primary">
                  {requirement.title}
                </h1>
                <p className="text-sm text-text-enterprise-secondary">
                  {requirement.client_name || 'Client not specified'}
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3">
              <Button
                onClick={handleViewCandidates}
                variant="outline"
                className="flex items-center gap-2"
              >
                <Users className="w-4 h-4" />
                View Candidates
              </Button>
              
              {hasPermission('requirements:write') && (
                <Button
                  onClick={handleEdit}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <Edit className="w-4 h-4" />
                  Edit
                </Button>
              )}
              
              {hasPermission('requirements:delete') && (
                <Button
                  onClick={handleDelete}
                  variant="destructive"
                  className="flex items-center gap-2"
                  disabled={deleteMutation.isPending}
                >
                  {deleteMutation.isPending ? (
                    <LoadingSpinner size="sm" variant="ai" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                  Delete
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <nav className="flex items-center gap-2 text-sm text-text-enterprise-tertiary">
          <button
            onClick={handleBack}
            className="hover:text-text-enterprise-primary transition-colors"
          >
            Requirements
          </button>
          <span>/</span>
          <span className="text-text-enterprise-primary font-medium">{requirement.title}</span>
        </nav>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <div className="space-y-6">
          {/* Basic Information */}
          <div className="glass-card backdrop-blur-xl border-glass-border dark:border-glass-border-dark-mode shadow-2xl rounded-xl p-6">
            <h2 className="text-lg font-bold text-text-enterprise-primary mb-4 flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-primary-500" />
              Basic Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="space-y-2">
                <label className="text-sm font-medium text-text-enterprise-secondary">Job Title</label>
                <p className="text-text-enterprise-primary font-semibold">{requirement.title}</p>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium text-text-enterprise-secondary">Client</label>
                <p className="text-text-enterprise-primary font-semibold">{requirement.client_name || 'Not specified'}</p>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium text-text-enterprise-secondary">Positions</label>
                <p className="text-text-enterprise-primary font-semibold">{requirement.positions || 1}</p>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium text-text-enterprise-secondary">Status</label>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  requirement.status === 'active' 
                    ? 'bg-success-500/20 text-success-700 dark:text-success-300' 
                    : 'bg-warning-500/20 text-warning-700 dark:text-warning-300'
                }`}>
                  {requirement.status}
                </span>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium text-text-enterprise-secondary">Priority</label>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  requirement.priority === 'urgent' 
                    ? 'bg-error-500/20 text-error-700 dark:text-error-300'
                    : requirement.priority === 'high'
                    ? 'bg-warning-500/20 text-warning-700 dark:text-warning-300'
                    : 'bg-info-500/20 text-info-700 dark:text-info-300'
                }`}>
                  {requirement.priority}
                </span>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium text-text-enterprise-secondary">Employment Type</label>
                <p className="text-text-enterprise-primary font-semibold">{requirement.employment_type || 'Not specified'}</p>
              </div>
            </div>
          </div>

          {/* Boolean Queries */}
          {requirement.boolean_queries && (
            <>
              {/* Upgrade Button - Show only for old format (no 5-level queries) */}
              {!requirement.boolean_queries.queries && 
               requirement.boolean_queries.manual_query && (
                <div className="mb-4 p-4 bg-gradient-to-r from-primary-500/10 to-info-500/10 border border-primary-500/30 rounded-xl">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <h3 className="text-base font-semibold text-text-enterprise-primary mb-1 flex items-center gap-2">
                        <AlertCircle className="w-5 h-5 text-primary-500" />
                        Upgrade to 5-Level Boolean Queries
                      </h3>
                      <p className="text-sm text-text-enterprise-secondary mb-3">
                        Get AI-powered Boolean search queries with 5 precision levels (Query 1 to Query 5) 
                        optimized for Naukri. Includes skill tiering, synonyms, and smart keyword expansion.
                      </p>
                      {requirement.boolean_queries.regeneration_status === 'processing' && (
                        <p className="text-sm text-info-600 dark:text-info-400 font-medium">
                          🔄 Generation in progress... Refresh in ~5 minutes
                        </p>
                      )}
                      {requirement.boolean_queries.regeneration_status === 'failed' && (
                        <p className="text-sm text-error-600 dark:text-error-400 font-medium">
                          ❌ Generation failed: {requirement.boolean_queries.regeneration_error || 'Unknown error'}
                        </p>
                      )}
                    </div>
                    <Button
                      onClick={handleRegenerateQueries}
                      disabled={regenerateMutation.isPending || requirement.boolean_queries.regeneration_status === 'processing'}
                      className="flex-shrink-0"
                      variant="default"
                    >
                      {regenerateMutation.isPending || requirement.boolean_queries.regeneration_status === 'processing' ? (
                        <>🔄 Generating...</>
                      ) : (
                        <>✨ Generate 5-Level Queries</>
                      )}
                    </Button>
                  </div>
                </div>
              )}

              <div className="glass-card backdrop-blur-xl border-glass-border shadow-2xl rounded-xl p-6 mb-8">
                <h2 className="text-lg font-bold text-text-enterprise-primary mb-4 flex items-center gap-2">
                  <Search className="w-5 h-5 text-primary-500" />
                  Boolean Search Queries for Naukri
                </h2>
                
                {/* Check if we have 5-level queries (new format) */}
                {requirement.boolean_queries.queries ? (
                <div className="space-y-4">
                  {/* Skill Analysis Summary */}
                  {requirement.boolean_queries.skill_analysis && (
                    <div className="bg-info-500/10 border border-info-500/30 rounded-lg p-4 mb-4">
                      <h3 className="text-sm font-semibold text-info-700 dark:text-info-300 mb-2">Skill Tiering</h3>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-info-600 dark:text-info-400 font-medium">Tier A (Core):</span>
                          <span className="ml-2 text-text-enterprise-secondary">{requirement.boolean_queries.skill_analysis.tier_a_skills?.join(', ')}</span>
                        </div>
                        <div>
                          <span className="text-info-600 dark:text-info-400 font-medium">Tier B (Flexible):</span>
                          <span className="ml-2 text-text-enterprise-secondary">{requirement.boolean_queries.skill_analysis.tier_b_skills?.join(', ')}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 5-Level Queries */}
                  {['level_100', 'level_90', 'level_80', 'level_70', 'level_60'].map((level) => {
                    const queryData = requirement.boolean_queries.queries[level];
                    if (!queryData) return null;
                    
                    const levelColors: Record<string, string> = {
                      level_100: 'bg-success-500/10 border-success-500/30',
                      level_90: 'bg-primary-500/10 border-primary-500/30',
                      level_80: 'bg-info-500/10 border-info-500/30',
                      level_70: 'bg-warning-500/10 border-warning-500/30',
                      level_60: 'bg-gray-500/10 border-gray-500/30'
                    };
                    
                    const levelLabels: Record<string, string> = {
                      level_100: 'Query 1',
                      level_90: 'Query 2',
                      level_80: 'Query 3',
                      level_70: 'Query 4',
                      level_60: 'Query 5'
                    };

                    return (
                      <div key={level} className={`${levelColors[level]} border rounded-lg p-4`}>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <h3 className="text-base font-semibold text-text-enterprise-primary">{levelLabels[level]}</h3>
                            <span className="text-xs px-2 py-1 bg-glass-surface/50 dark:bg-glass-surface-dark-mode/50 rounded-full text-text-enterprise-secondary">
                              {queryData.kept_skills?.length || 0} skills
                            </span>
                          </div>
                          <Button
                            onClick={() => {
                              navigator.clipboard.writeText(queryData.query || '');
                              toast.success(`${levelLabels[level]} query copied to clipboard`);
                            }}
                            variant="ghost"
                            size="sm"
                            className="p-1"
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="bg-glass-surface/50 dark:bg-glass-surface-dark-mode/50 border border-glass-border/30 dark:border-glass-border-dark-mode/30 rounded-lg p-3 mb-2">
                          <code className="text-xs text-text-enterprise-primary font-mono break-all">
                            {queryData.query || 'No query generated'}
                          </code>
                        </div>
                        <div className="flex items-start justify-between text-sm">
                          <p className="text-text-enterprise-secondary flex-1">{queryData.description}</p>
                          <span className="text-xs text-text-enterprise-tertiary ml-4 whitespace-nowrap">
                            {queryData.query?.length || 0} chars
                          </span>
                        </div>
                        {queryData.kept_skills && queryData.kept_skills.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {queryData.kept_skills.map((skill: string, idx: number) => (
                              <span key={idx} className="text-xs px-2 py-0.5 bg-glass-surface/30 dark:bg-glass-surface-dark-mode/30 rounded text-text-enterprise-secondary">
                                {skill}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Usage Tips */}
                  {requirement.boolean_queries.usage_tips && requirement.boolean_queries.usage_tips.length > 0 && (
                    <div className="bg-info-500/10 border border-info-500/30 rounded-lg p-4 mt-4">
                      <h3 className="text-base font-semibold text-info-700 dark:text-info-300 mb-3">💡 Usage Tips</h3>
                      <ul className="space-y-2">
                        {requirement.boolean_queries.usage_tips.map((tip: string, index: number) => (
                          <li key={index} className="text-sm text-info-600 dark:text-info-400 flex items-start gap-2">
                            <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            {tip}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ) : (
                // Old format (manual/ai/hybrid) - backward compatibility
                <div className="space-y-6">
                  {/* Manual Query */}
                  {requirement.boolean_queries.manual_query && (
                    <div className="bg-glass-surface/30 dark:bg-glass-surface-dark-mode/30 border border-glass-border/50 dark:border-glass-border-dark-mode/50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-base font-semibold text-text-enterprise-primary">Manual Query</h3>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-text-enterprise-secondary">
                            {requirement.boolean_queries.manual_query.target_platforms?.join(', ')}
                          </span>
                          <Button
                            onClick={() => {
                              navigator.clipboard.writeText(requirement.boolean_queries.manual_query.query || '');
                              toast.success('Manual query copied to clipboard');
                            }}
                            variant="ghost"
                            size="sm"
                            className="p-1"
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="bg-glass-surface/50 dark:bg-glass-surface-dark-mode/50 border border-glass-border/30 dark:border-glass-border-dark-mode/30 rounded-lg p-3 mb-2">
                        <code className="text-sm text-text-enterprise-primary font-mono">
                          {requirement.boolean_queries.manual_query.query || 'No query generated'}
                        </code>
                      </div>
                      {requirement.boolean_queries.manual_query.description && (
                        <p className="text-sm text-text-enterprise-secondary">
                          {requirement.boolean_queries.manual_query.description}
                        </p>
                      )}
                    </div>
                  )}

                  {/* AI Query */}
                  {requirement.boolean_queries.ai_query && (
                    <div className="bg-glass-surface/30 dark:bg-glass-surface-dark-mode/30 border border-glass-border/50 dark:border-glass-border-dark-mode/50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-base font-semibold text-text-enterprise-primary">AI Query</h3>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-text-enterprise-secondary">
                            {requirement.boolean_queries.ai_query.target_platforms?.join(', ')}
                          </span>
                          <Button
                            onClick={() => {
                              navigator.clipboard.writeText(requirement.boolean_queries.ai_query.query || '');
                              toast.success('AI query copied to clipboard');
                            }}
                            variant="ghost"
                            size="sm"
                            className="p-1"
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="bg-glass-surface/50 dark:bg-glass-surface-dark-mode/50 border border-glass-border/30 dark:border-glass-border-dark-mode/30 rounded-lg p-3 mb-2">
                        <code className="text-sm text-text-enterprise-primary font-mono">
                          {requirement.boolean_queries.ai_query.query || 'No query generated'}
                        </code>
                      </div>
                      {requirement.boolean_queries.ai_query.description && (
                        <p className="text-sm text-text-enterprise-secondary">
                          {requirement.boolean_queries.ai_query.description}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Hybrid Query */}
                  {requirement.boolean_queries.hybrid_query && (
                    <div className="bg-glass-surface/30 dark:bg-glass-surface-dark-mode/30 border border-glass-border/50 dark:border-glass-border-dark-mode/50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-base font-semibold text-text-enterprise-primary">Hybrid Query</h3>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-text-enterprise-secondary">
                            {requirement.boolean_queries.hybrid_query.target_platforms?.join(', ')}
                          </span>
                          <Button
                            onClick={() => {
                              navigator.clipboard.writeText(requirement.boolean_queries.hybrid_query.query || '');
                              toast.success('Hybrid query copied to clipboard');
                            }}
                            variant="ghost"
                            size="sm"
                            className="p-1"
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="bg-glass-surface/50 dark:bg-glass-surface-dark-mode/50 border border-glass-border/30 dark:border-glass-border-dark-mode/30 rounded-lg p-3 mb-2">
                        <code className="text-sm text-text-enterprise-primary font-mono">
                          {requirement.boolean_queries.hybrid_query.query || 'No query generated'}
                        </code>
                      </div>
                      {requirement.boolean_queries.hybrid_query.description && (
                        <p className="text-sm text-text-enterprise-secondary">
                          {requirement.boolean_queries.hybrid_query.description}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Search Tips */}
                  {requirement.boolean_queries.search_tips && requirement.boolean_queries.search_tips.length > 0 && (
                    <div className="bg-info-500/10 border border-info-500/30 rounded-lg p-4">
                      <h3 className="text-base font-semibold text-info-700 dark:text-info-300 mb-3">Search Tips</h3>
                      <ul className="space-y-2">
                        {requirement.boolean_queries.search_tips.map((tip: string, index: number) => (
                          <li key={index} className="text-sm text-info-600 dark:text-info-400 flex items-start gap-2">
                            <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            {tip}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
              </div>
            </>
          )}

          {/* Description */}
          {requirement.description && (
            <div className="glass-card backdrop-blur-xl border-glass-border dark:border-glass-border-dark-mode shadow-2xl rounded-xl p-6">
              <h2 className="text-lg font-bold text-text-enterprise-primary mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary-500" />
                Job Description
              </h2>
              <div className="prose prose-sm max-w-none text-text-enterprise-primary">
                <div className="whitespace-pre-wrap">{requirement.description}</div>
              </div>
            </div>
          )}

          {/* Skills */}
          <div className="glass-card backdrop-blur-xl border-glass-border dark:border-glass-border-dark-mode shadow-2xl rounded-xl p-6">
            <h2 className="text-lg font-bold text-text-enterprise-primary mb-4 flex items-center gap-2">
              <Target className="w-5 h-5 text-primary-500" />
              Skills & Requirements
            </h2>
            
            {(() => {
              // Handle both formats: mandatory/optional (without underscores) and mandatory_skills/optional_skills (with underscores)
              const mandatorySkills = requirement.skills?.mandatory || requirement.skills?.mandatory_skills || [];
              const optionalSkills = requirement.skills?.optional || requirement.skills?.optional_skills || [];
              
              return (
                <>
                  {mandatorySkills.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-sm font-semibold text-text-enterprise-secondary mb-3">Mandatory Skills</h3>
                      <div className="flex flex-wrap gap-2">
                        {mandatorySkills.map((skill: string, index: number) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-error-500/20 text-error-700 dark:text-error-300 border border-error-500/30"
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {optionalSkills.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-text-enterprise-secondary mb-3">Optional Skills</h3>
                      <div className="flex flex-wrap gap-2">
                        {optionalSkills.map((skill: string, index: number) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-info-500/20 text-info-700 dark:text-info-300 border border-info-500/30"
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {mandatorySkills.length === 0 && optionalSkills.length === 0 && (
                    <p className="text-text-enterprise-secondary text-sm">No skills specified</p>
                  )}
                </>
              );
            })()}
          </div>

          {/* Experience & Education */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Experience */}
            <div className="glass-card backdrop-blur-xl border-glass-border dark:border-glass-border-dark-mode shadow-2xl rounded-xl p-6">
              <h2 className="text-lg font-bold text-text-enterprise-primary mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-primary-500" />
                Experience
              </h2>
              <div className="space-y-3">
                {requirement.experience?.min_years && (
                  <div>
                    <label className="text-sm font-medium text-text-enterprise-secondary">Minimum Experience</label>
                    <p className="text-text-enterprise-primary font-semibold">{requirement.experience.min_years} years</p>
                  </div>
                )}
                {requirement.experience?.max_years && (
                  <div>
                    <label className="text-sm font-medium text-text-enterprise-secondary">Maximum Experience</label>
                    <p className="text-text-enterprise-primary font-semibold">{requirement.experience.max_years} years</p>
                  </div>
                )}
                {requirement.seniority && (
                  <div>
                    <label className="text-sm font-medium text-text-enterprise-secondary">Seniority Level</label>
                    <p className="text-text-enterprise-primary font-semibold">{requirement.seniority}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Education */}
            <div className="glass-card backdrop-blur-xl border-glass-border dark:border-glass-border-dark-mode shadow-2xl rounded-xl p-6">
              <h2 className="text-lg font-bold text-text-enterprise-primary mb-4 flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-primary-500" />
                Education
              </h2>
              <div className="space-y-3">
                {requirement.education && (
                  <div>
                    <label className="text-sm font-medium text-text-enterprise-secondary">Education Requirements</label>
                    <p className="text-text-enterprise-primary font-semibold">{requirement.education}</p>
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium text-text-enterprise-secondary">Notice Period</label>
                  <p className="text-text-enterprise-primary font-semibold">
                    {requirement.notice_period !== undefined && requirement.notice_period !== null 
                      ? `${requirement.notice_period} days` 
                      : '30 days (default)'}
                  </p>
                </div>
                {requirement.category && (
                  <div>
                    <label className="text-sm font-medium text-text-enterprise-secondary">Category</label>
                    <p className="text-text-enterprise-primary font-semibold">{requirement.category}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Location */}
          {requirement.location && (
            <div className="glass-card backdrop-blur-xl border-glass-border dark:border-glass-border-dark-mode shadow-2xl rounded-xl p-6">
              <h2 className="text-lg font-bold text-text-enterprise-primary mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-primary-500" />
                Location
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {requirement.location.city && (
                  <div>
                    <label className="text-sm font-medium text-text-enterprise-secondary">City</label>
                    <p className="text-text-enterprise-primary font-semibold">{requirement.location.city}</p>
                  </div>
                )}
                {requirement.location.state && (
                  <div>
                    <label className="text-sm font-medium text-text-enterprise-secondary">State</label>
                    <p className="text-text-enterprise-primary font-semibold">{requirement.location.state}</p>
                  </div>
                )}
                {requirement.location.country && (
                  <div>
                    <label className="text-sm font-medium text-text-enterprise-secondary">Country</label>
                    <p className="text-text-enterprise-primary font-semibold">{requirement.location.country}</p>
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium text-text-enterprise-secondary">Remote Work</label>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    requirement.location.remote_work 
                      ? 'bg-success-500/20 text-success-700 dark:text-success-300' 
                      : 'bg-warning-500/20 text-warning-700 dark:text-warning-300'
                  }`}>
                    {requirement.location.remote_work ? 'Yes' : 'No'}
                  </span>
                </div>
                <div>
                  <label className="text-sm font-medium text-text-enterprise-secondary">Relocation Assistance</label>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    requirement.location.relocation_assistance 
                      ? 'bg-success-500/20 text-success-700 dark:text-success-300' 
                      : 'bg-warning-500/20 text-warning-700 dark:text-warning-300'
                  }`}>
                    {requirement.location.relocation_assistance ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Compensation */}
          {requirement.compensation && (
            <div className="glass-card backdrop-blur-xl border-glass-border dark:border-glass-border-dark-mode shadow-2xl rounded-xl p-6">
              <h2 className="text-lg font-bold text-text-enterprise-primary mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-primary-500" />
                Compensation
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {requirement.compensation.salary_range && (
                  <div>
                    <label className="text-sm font-medium text-text-enterprise-secondary">Salary Range</label>
                    <p className="text-text-enterprise-primary font-semibold">
                      {requirement.compensation.salary_range.min && requirement.compensation.salary_range.max
                        ? `${requirement.compensation.salary_range.min.toLocaleString()} - ${requirement.compensation.salary_range.max.toLocaleString()} ${requirement.compensation.salary_range.currency || ''}`
                        : 'Not specified'
                      }
                    </p>
                  </div>
                )}
                {requirement.compensation.benefits && requirement.compensation.benefits.length > 0 && (
                  <div>
                    <label className="text-sm font-medium text-text-enterprise-secondary">Benefits</label>
                    <div className="flex flex-wrap gap-1 mt-1">
                          {requirement.compensation.benefits.map((benefit: string, index: number) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-info-500/20 text-info-700 dark:text-info-300"
                        >
                          {benefit}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium text-text-enterprise-secondary">Equity</label>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    requirement.compensation.equity 
                      ? 'bg-success-500/20 text-success-700 dark:text-success-300' 
                      : 'bg-warning-500/20 text-warning-700 dark:text-warning-300'
                  }`}>
                    {requirement.compensation.equity ? 'Yes' : 'No'}
                  </span>
                </div>
                <div>
                  <label className="text-sm font-medium text-text-enterprise-secondary">Bonus</label>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    requirement.compensation.bonus 
                      ? 'bg-success-500/20 text-success-700 dark:text-success-300' 
                      : 'bg-warning-500/20 text-warning-700 dark:text-warning-300'
                  }`}>
                    {requirement.compensation.bonus ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Additional Details */}
          {requirement.additional_details && (
            <div className="glass-card backdrop-blur-xl border-glass-border dark:border-glass-border-dark-mode shadow-2xl rounded-xl p-6">
              <h2 className="text-lg font-bold text-text-enterprise-primary mb-4 flex items-center gap-2">
                <Settings className="w-5 h-5 text-primary-500" />
                Additional Details
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {requirement.additional_details.travel_requirements && (
                  <div>
                    <label className="text-sm font-medium text-text-enterprise-secondary">Travel Requirements</label>
                    <p className="text-text-enterprise-primary font-semibold">{requirement.additional_details.travel_requirements}</p>
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium text-text-enterprise-secondary">Visa Sponsorship</label>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    requirement.additional_details.visa_sponsorship 
                      ? 'bg-success-500/20 text-success-700 dark:text-success-300' 
                      : 'bg-warning-500/20 text-warning-700 dark:text-warning-300'
                  }`}>
                    {requirement.additional_details.visa_sponsorship ? 'Yes' : 'No'}
                  </span>
                </div>
                <div>
                  <label className="text-sm font-medium text-text-enterprise-secondary">Security Clearance</label>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    requirement.additional_details.security_clearance 
                      ? 'bg-success-500/20 text-success-700 dark:text-success-300' 
                      : 'bg-warning-500/20 text-warning-700 dark:text-warning-300'
                  }`}>
                    {requirement.additional_details.security_clearance ? 'Yes' : 'No'}
                  </span>
                </div>
                {requirement.additional_details.certifications && requirement.additional_details.certifications.length > 0 && (
                  <div className="md:col-span-2 lg:col-span-3">
                    <label className="text-sm font-medium text-text-enterprise-secondary">Certifications</label>
                    <div className="flex flex-wrap gap-2 mt-1">
                          {requirement.additional_details.certifications.map((cert: string, index: number) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-500/20 text-primary-700 dark:text-primary-300 border border-primary-500/30"
                        >
                          {cert}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}


          {/* Metadata */}
          <div className="glass-card backdrop-blur-xl border-glass-border dark:border-glass-border-dark-mode shadow-2xl rounded-xl p-6">
            <h2 className="text-lg font-bold text-text-enterprise-primary mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary-500" />
              Metadata
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <label className="text-sm font-medium text-text-enterprise-secondary">Created At</label>
                <p className="text-text-enterprise-primary font-semibold">
                  {new Date(requirement.created_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-text-enterprise-secondary">Updated At</label>
                <p className="text-text-enterprise-primary font-semibold">
                  {new Date(requirement.updated_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-text-enterprise-secondary">Created By</label>
                <p className="text-text-enterprise-primary font-semibold">{requirement.created_by || 'Unknown'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequirementDetailsPage;
